import { Draft, DraftSender } from "./inboxDraft.types";

import type { SenderRecipientOption } from "../components/SenderRecipientDropdown";

/** Sender and recipient must be on the same channel to be sendable. */
export const hasDraftChannelMismatch = (draft: Draft): boolean =>
	!!draft.from?.type && !!draft.to?.type && draft.from.type !== draft.to.type;

/**
 * A draft that Send will send: it has content, or it is an invite note (LinkedIn allows a
 * connection request without a note). Moved out of `useAddInboxChat` so the sender warning reads
 * the same answer (PREC-4312).
 */
export const isSendableDraft = (draft: Draft): boolean =>
	draft.content.trim().length > 0 || !!draft.isInviteNote;

/** Email drafts require a subject line. */
export const hasMissingSubject = (draft: Draft): boolean =>
	draft.from?.type === "email" && !draft.subject?.trim();

/**
 * One-line preview of a draft for the collapsed editor strip: whitespace
 * collapsed so multi-paragraph drafts read as a single line ("Hallo Daniel,
 * vielen Dank …"). Empty when the draft has no content yet.
 */
export const draftStripPreview = (content: string): string => content.replace(/\s+/g, " ").trim();

/**
 * The composer's option for a draft's sender, by identity: the LinkedIn account of that user, or
 * that user's exact mailbox. `undefined` when the composer offers no such sender.
 */
export const findDraftSenderOption = (
	from: DraftSender | undefined,
	senderOptions: SenderRecipientOption[]
): SenderRecipientOption | undefined =>
	from?.userId
		? senderOptions.find(
				(o) =>
					o.type === from.type &&
					o.userId === from.userId &&
					(from.type !== "email" || o.emailId === from.emailId)
			)
		: undefined;

export type DraftSenderBlock =
	/** No real sender: "Select sender", a placeholder, or a sender not (or no longer) offered. */
	| { kind: "missing" }
	/** The sender is offered but cannot send, e.g. a mailbox that needs a reconnect. */
	| { kind: "unavailable"; option: SenderRecipientOption };

/**
 * Why a draft may not be sent as it stands, as far as its sender goes, or `null` (PREC-4312). A
 * draft without a real sender used to go out anyway: the API then took the thread's sender, so a
 * reply went out as a colleague without anyone choosing that.
 */
export const draftSenderBlock = (
	draft: Draft,
	senderOptions: SenderRecipientOption[]
): DraftSenderBlock | null => {
	const option = findDraftSenderOption(draft.from, senderOptions);

	if (!option) {
		return { kind: "missing" };
	}

	return option.disabled ? { kind: "unavailable", option } : null;
};
